import { SB } from '@play-co/replicant';

/** Rolling send/receive limit history entry for one card transfer. */
const cardTradeSchema = SB.object({
  /** Card id that was sent or received. */
  cardId: SB.string(),
  /** Timestamp when the transfer was recorded. */
  timestamp: SB.int(),
});

/** Pending async card trade stored during the trade lifecycle. */
const cardPendingTradeSchema = SB.object({
  /** Stable trade id shared by both participants. */
  tradeId: SB.string(),
  /** Player id for the user offering the original card. */
  proposerId: SB.string(),
  /** Player id for the user receiving and optionally countering the trade. */
  recipientId: SB.string(),
  /** Display name for the other trade participant from this user's view. */
  otherUserName: SB.string().optional(),
  /** Avatar image URL for the other trade participant from this user's view. */
  otherUserAvatar: SB.string().optional(),
  /** Proposer card ids escrowed by removing them from proposer inventory. */
  offeredCardIds: SB.array(SB.string()),
  /** Recipient counter-offer card ids escrowed after the trade is countered. */
  counterCardIds: SB.array(SB.string()).optional(),
  /** Trade lifecycle state: proposed before countering, countered afterward. */
  status: SB.tuple(['proposed', 'countered'] as const),
  /** Timestamp when the trade was first created. */
  createdAt: SB.int(),
  /** Timestamp when the trade was last updated. */
  updatedAt: SB.int(),
  /** Timestamp when the recipient confirmed their counter offer. */
  recipientConfirmedAt: SB.int().optional(),
  /** Timestamp when the proposer confirmed completion of the trade. */
  proposerConfirmedAt: SB.int().optional(),
});

/** Card inventory counts keyed by card id. */
const cardsInventorySchema = SB.map(SB.number());

/**
 * Legacy cards event state kept for backwards compatibility.
 *
 * @deprecated Current cards collection state uses the top-level fields on
 * `cardsState`.
 */
const cardsEventSchema = SB.object({
  /** Timestamp when the legacy event started. */
  startAt: SB.int(),
  /** Timestamp when the legacy event ended. */
  endAt: SB.int(),
  /** Timestamp when the user last viewed the legacy event. */
  seenAt: SB.int(),
  /** Owned card counts keyed by card id. */
  cardsOwned: cardsInventorySchema,
  /** Received-card ledger keyed by sender player id, then card id. */
  cardsReceived: SB.map(cardsInventorySchema),
  /** Newly-added card counts keyed by card id. */
  cardsNewlyAdded: cardsInventorySchema,
  /** Claimed collection or album rewards keyed by reward id. */
  rewardsClaimed: SB.map(SB.boolean()).optional(),
  /** Card send, receive, and pending-trade records. */
  cardTrades: SB.object({
    /** Sent-card records that may count against the send limit. */
    cardsSent: SB.array(cardTradeSchema),
    /** Received-card records that may count against the receive limit. */
    cardsReceived: SB.array(cardTradeSchema),
    /** Pending trades keyed by trade id, including completed recipient markers. */
    pendingTrades: SB.map(cardPendingTradeSchema).optional(),
    /** Number of received cards discarded by receive limits before collection. */
    cardsDiscarded: SB.number(),
  }),
});

/** Schema for the cards state. */
export const cardsState = SB.object({
  /** @deprecated Legacy event state kept for backwards compatibility. */
  events: SB.map(cardsEventSchema).optional(),
  /** Timestamp when the user last viewed the cards album. */
  seenAt: SB.int(),
  /** Owned card counts keyed by card id. */
  cardsOwned: cardsInventorySchema,
  /** Received-card ledger keyed by sender player id, then card id. */
  cardsReceived: SB.map(cardsInventorySchema),
  /** Newly-added card counts keyed by card id, cleared after the user sees them. */
  cardsNewlyAdded: cardsInventorySchema,
  /** Claimed collection or album rewards keyed by reward id. */
  rewardsClaimed: SB.map(SB.boolean()).optional(),
  /** Card limit histories and pending-trade records. */
  cardTrades: SB.object({
    /** Sent-card records that may count against the send limit. */
    cardsSent: SB.array(cardTradeSchema),
    /** Received-card records that may count against the receive limit. */
    cardsReceived: SB.array(cardTradeSchema),
    /**
     * Trade initiation timestamps retained independently of `pendingTrades`.
     * Completed or rejected trades are removed before the JST daily limit
     * resets, so their timestamps are needed to enforce the full-day cap.
     * Entries outside the current limit window are lazily pruned when another
     * trade is recorded or trade cleanup runs, preventing unbounded growth.
     */
    tradesStartedAt: SB.array(SB.int()),
    /** Pending trades keyed by trade id, including completed recipient markers. */
    pendingTrades: SB.map(cardPendingTradeSchema).optional(),
    /** Number of received cards discarded by receive limits before collection. */
    cardsDiscarded: SB.number(),
  }),
});

/** Cards collection state type. */
export type CardsState = SB.ExtractType<typeof cardsState>;

/** Pending async card trade state type. */
export type CardPendingTrade = SB.ExtractType<typeof cardPendingTradeSchema>;

/** Optional cards collection slice added to the main Replicant state. */
export const cardsSchema = {
  cards: cardsState.optional(),
};
